import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

const listDepthPluginKey = new PluginKey("listDepthClasses");

const isList = (node: ProseMirrorNode) =>
  node.type.name === "bulletList" || node.type.name === "orderedList";

// Nesting depth alternates the list marker every 2 levels. CSS can express this
// per depth but not as modulo, so the depth class carries `depth % 2` and the
// stylesheet only needs two rules. Depth is counted freshly from each list's
// ancestors (never a running counter, which would over-count sibling lists).
// bulletList and orderedList share the counter so mixed ul/ol nesting keeps
// advancing, but get separate class namespaces for their own list-style-type.
const buildDecorations = (doc: ProseMirrorNode): DecorationSet => {
  const decorations: Decoration[] = [];

  doc.descendants((node, pos) => {
    if (!isList(node)) return;

    const $pos = doc.resolve(pos);
    let depth = 0;
    for (let d = $pos.depth; d > 0; d--) {
      if (isList($pos.node(d))) depth++;
    }

    const prefix = node.type.name === "bulletList" ? "list-depth" : "list-ordered-depth";
    decorations.push(
      Decoration.node(pos, pos + node.nodeSize, { class: `${prefix}-${depth % 2}` }),
    );
  });

  return DecorationSet.create(doc, decorations);
};

export const ListDepthClasses = Extension.create({
  name: "listDepthClasses",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: listDepthPluginKey,
        state: {
          init: (_config, state) => buildDecorations(state.doc),
          apply: (tr, previous, _oldState, newState) =>
            tr.docChanged ? buildDecorations(newState.doc) : previous,
        },
        props: {
          decorations(state) {
            return listDepthPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
